type ChunkErrorContext = {
  trigger: 'error-event' | 'unhandledrejection' | 'vite:preloadError';
  errorMessage?: string | undefined;
  filename?: string | undefined;
  alreadyReloaded: boolean;
};

type CaptureMessage = (message: string, extra?: ChunkErrorContext) => void;

export const RELOAD_FLAG = 'chunk-error-reloaded';

/**
 * Give the current session its one-reload budget back once the app has booted
 * successfully. Call this only after we're confident the current chunks loaded
 * fine — so a *second* stale chunk later in a long session (e.g. across a
 * subsequent deploy) can also auto-heal instead of dead-ending on the fallback
 * screen. Without this reset the flag lives for the whole sessionStorage
 * lifetime, permitting a single auto-reload per session ever.
 */
export const clearChunkReloadFlag = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // sessionStorage can throw in locked-down/in-app browsers — ignore.
  }
};

const isChunkErrorMessage = (message: string): boolean =>
  message.includes('Importing a module script failed') ||
  message.includes('text/html') ||
  message.includes('Loading chunk') ||
  message.includes('Unable to preload CSS for');

const reloadOnce = (captureMessage: CaptureMessage, extra: Omit<ChunkErrorContext, 'alreadyReloaded'>): void => {
  const alreadyReloaded = !!sessionStorage.getItem(RELOAD_FLAG);
  if (alreadyReloaded) {
    // The auto-reload did NOT fix it — this is a genuine, actionable problem.
    // Reporting only here (not on the first, auto-healed hit) is what keeps
    // Sentry from drowning in benign per-deploy chunk churn. SN-8114.
    captureMessage('Stale chunk hit', { ...extra, alreadyReloaded: true });
    return;
  }
  // First hit: heal silently. The reload picks up the fresh index.html + chunks.
  sessionStorage.setItem(RELOAD_FLAG, 'true');
  window.location.reload();
};

export const setupChunkErrorHandler = (captureMessage: CaptureMessage): void => {
  // Remove the early inline-script listeners injected by earlyChunkGuardPlugin so they
  // don't double-fire alongside these more capable handlers.
  (window as Window & { __removeEarlyChunkGuard?: () => void }).__removeEarlyChunkGuard?.();

  window.addEventListener('error', (event) => {
    const isChunkError = isChunkErrorMessage(event.message ?? '');
    if (isChunkError) {
      reloadOnce(captureMessage, {
        trigger: 'error-event',
        errorMessage: event.message,
        filename: event.filename,
      });
    }
  });

  // iOS Safari fires dynamic import failures as unhandledrejection rather than error events.
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari) {
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason;
      const message = reason instanceof Error ? reason.message : String(reason ?? '');
      if (isChunkErrorMessage(message)) {
        event.preventDefault();
        reloadOnce(captureMessage, {
          trigger: 'unhandledrejection',
          errorMessage: message,
        });
      }
    });
  }

  // Vite CSS/JS preload errors caused by stale assets after deployments.
  // event.payload is the underlying Error (typed by vite/client's VitePreloadErrorEvent).
  // CSS errors: message = "Unable to preload CSS for <url>" — URL is in the message.
  // JS errors: message = "'text/html' is not a valid JavaScript MIME type." — URL is in the stack.
  // Guard with isChunkErrorMessage: third-party injected scripts (e.g. iOS in-app browsers)
  // can also trigger vite:preloadError with unrelated messages — don't reload for those.
  // A missing/undefined payload message is not a chunk error either.
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: Error }).payload;
    // Always prevent to avoid unhandled-rejection noise from iOS in-app browsers
    // (e.g. Google app / WKWebView) which can trigger vite:preloadError with
    // non-chunk messages. The reload is still guarded below — only real stale
    // chunk errors trigger a page reload.
    event.preventDefault();
    if (!payload?.message || !isChunkErrorMessage(payload.message)) return;
    
    const message = payload?.message;
    const cssMatch = message?.match(/Unable to preload CSS for (.+)/);
    const filename =
      cssMatch?.[1] ??
      payload?.stack?.match(/https?:\/\/\S+/)?.[0] ??
      undefined;
    reloadOnce(captureMessage, {
      trigger: 'vite:preloadError',
      errorMessage: message,
      filename,
    });
  });
};
